<template>
  <!-- Desktop button dropdown -->
  <v-btn
    v-if="!mobileMenuMode"
    color="base-white"
    icon
    class="language-button custom-rounded-icon d-none d-sm-flex"
  >
    <v-icon 
      :icon="appsIcon"
      color="base-white"
    />

    <v-tooltip
      activator="parent"
      location="bottom"
    >
      {{ tooltip }}
    </v-tooltip>

    <v-menu
      v-model="showMenu"
      activator="parent"
      width="230"
      offset="18"
    >
      <v-card class="px-2">
        <v-list density="compact">
          <template
            v-for="(item, index) in (appsMenu || localAppMenu)"
            :key="index"
          >
            <v-list-item
              v-if="!item.isDivider"
              @click="handleMenuItemClick(item)"
            >
              <template #prepend>
                <v-icon
                  v-if="typeof item.icon === 'string'"
                  class="app-switcher-icon"
                  :icon="item.icon"
                  color="base-blue-80"
                />
                <component
                  :is="item.icon"
                  v-else
                  class="crv-svg-icon"
                />
              </template>
              <v-list-item-title>{{ item.label }}</v-list-item-title>
            </v-list-item>

            <v-divider
              v-if="item.hasDividerBellow || item.isDivider"
              class="my-2"
            />
          </template>
        </v-list>
      </v-card>
    </v-menu>
  </v-btn>

  <!-- Mobile Menu Item -->
  <v-menu
    v-else
    width="230"
    offset="12"
  >
    <template #activator="{ props }">
      <v-list-item
        v-bind="props"
        role="button"
        rounded="lg"
        :prepend-icon="appsIcon"
        :append-icon="mdiMenuDown"
        :title="tooltip"
        class="hoverable-locale-item"
      />
    </template>

    <v-card class="px-2">
      <v-list density="compact">
        <template
          v-for="(item, index) in (appsMenu || localAppMenu)"
          :key="index"
        >
          <v-list-item
            v-if="!item.isDivider"
            @click="handleMenuItemClick(item)"
          >
            <template #prepend>
              <v-icon
                v-if="typeof item.icon === 'string'"
                class="app-switcher-icon"
                :icon="item.icon"
                color="base-blue-80"
              />
              <component
                :is="item.icon"
                v-else
                class="crv-svg-icon"
              />
            </template>
            <v-list-item-title>{{ item.label }}</v-list-item-title>
          </v-list-item>

          <v-divider
            v-if="item.hasDividerBellow || item.isDivider"
            class="my-2"
          />
        </template>
      </v-list>
    </v-card>
  </v-menu>
</template>


<script>
import { mdiApps, mdiMenuDown } from '@mdi/js'
import { getDefaultMenu } from '@v3c/helpers/defaultAppMenu'


export default {
    name: 'CrvAppMenu',

    props: {

      tooltip: {
            type: String,
            default: 'Other CRV apps',
        },

        appsMenu: {
            type: Object,
            default: null,
            note: `As example, the following appsMenu might be provided if the default does not meet the requirements.
            however; the defaultAppMenu.js can provide translations and alternative defaults per marketCountryCode. Expand on it if needed.
            External links will open in a new tab, internal links will use the Vue Router to navigate.
            
            () => ({
                CrvAnimal: {
                    id: 'CrvAnimal',
                    label: 'CRV Dier',
                    icon: CrvAnimal,
                    url: 'crv-dier.nl',
                    isExternal: true,
                    hasDividerBellow: true
                },
                Divider:{ isDivider: true},                
                CrvAdmin: {
                    id: 'CrvAdmin',
                    label: 'CRV Admin',
                    icon: mdiCog,
                    url: '/admin',
                    isExternal: false,
                    hasDividerBellow: false
                },
            }),`
        },

        hideDefaultAppMenuItems: {
          type: Array,
          default: () => [],
          note: `An array of app IDs that should be hidden from the default menu. This is useful if you want to
                  customize the default menu for specific users or roles. For example, ['CrvAnimal', 'CrvAdmin'] will hide
                  those apps from the default menu.`
        },

        marketCountryCode: {
            type: String,
            required: true,
            note: ` The marketCountryCode is a code defining the market of a user and ultimately the app menu
                    version that is offered to that user. This is not the same as the locale in which the app
                    menu is represented to the user. The marketCountryCode is used to pick the correct configuration
                    from the defaultAppMenu that currently resides in crv-chrome. When the appMenu is passed in from
                    the client app it is not necessary to also pass in a marketCountryCode, just pass in the appMenu
                    that is relevant for your market`,
        },

        mobileMenuMode: {
          type: Boolean,
          default: false,
        },
    },

    data() {
        return {
            environment: null,
            showMenu: false,
            appsIcon: mdiApps,
            mdiMenuDown,
        }
    },

    computed: {
        localAppMenu() {
          const filterMenu = menu => {
            return menu.filter(item => {
              if (!item.id) return true // keep dividers
              return !this.hideDefaultAppMenuItems.includes(item.id)
            })
          }

          if (this.appsMenu && Array.isArray(this.appsMenu)) {
            return filterMenu(this.appsMenu)
          }

          if (!this.environment || !this.marketCountryCode) {
            return []
          }

          const countryConfig = getDefaultMenu(this.marketCountryCode, this.environment)
          return filterMenu(Array.isArray(countryConfig) ? countryConfig : [])
        }
    },

    mounted() {
      const host = window.location.hostname;
      let envPrefix = host.split('.')[0];

      if (host.startsWith('localhost')) {
        envPrefix = 'dev';
      }

      this.environment = ['acc', 'dev', 'tst'].includes(envPrefix) ? envPrefix : 'prd';
    },

    methods: {
      // Do we still want this sorting functionality? Some apps need to remain on top apparently.
        // sort(apps) {
        //     return orderBy(apps, 'label', 'ASC')
        // },

        handleMenuItemClick(item) {
          if (item.isExternal) {
            window.open(item.url, '_blank')
          } else if (typeof item.url === 'string') {
            this.$router.push(item.url)
          } 
        },

        logMarketCountryCodeAndEnv() {
          console.log(`Market Country Code: ${this.marketCountryCode} & Environment: ${this.environment}`);
        },
    },
}
</script>


<style lang="scss" scoped>
@use '@v3c/assets/styles/variables.scss' as *;

    .crv-svg-icon {
        width: 24px !important;
        height: 24px !important;
        margin-right: 32px;
    }
    
    // Set opacity equal to crv-svg-icon
    i.v-icon.app-switcher-icon {
      opacity: 1;
    }
</style>



